import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard,
  Radar,
  MessagesSquare,
  Briefcase,
  Star,
  ShieldAlert,
  CalendarDays,
  Newspaper,
  GraduationCap,
  Landmark,
  LineChart,
  Crown,
  Lock,
  LogOut,
  ChevronDown,
  Globe,
  Share2,
  KeyRound,
  Bell,
  BellRing,
  Trash2,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { ChangePasswordDialog } from "./ChangePasswordDialog";
import { DeleteAccountDialog } from "./DeleteAccountDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";

const NAV = [
  { label: "Dashboard", path: "/dashboard", icon: LayoutDashboard },
  { label: "Market Scanner", path: "/scanner", icon: Radar },
  { label: "Research Assistant", path: "/assistant", icon: MessagesSquare },
  { label: "Watchlists", path: "/watchlists", icon: Star },
  { label: "Portfolio", path: "/portfolio", icon: Briefcase },
  { label: "Risk Tools", path: "/risk", icon: ShieldAlert },
  { label: "Dividends", path: "/dividends", icon: CalendarDays },
  { label: "Economic News", path: "/macro", icon: Newspaper },
  { label: "Learning Centre", path: "/learn", icon: GraduationCap },
  { label: "ISA & Tax", path: "/tax", icon: Landmark },
  { label: "Wealth Simulator", path: "/simulator", icon: LineChart },
];

export const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    api.get("/watchlists/alerts/triggered").then((r) => setAlerts(r.data.triggered)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside
        className="w-60 shrink-0 border-r border-border bg-card flex flex-col fixed inset-y-0 left-0 z-40"
        data-testid="app-sidebar"
      >
        <div className="px-5 py-6 border-b border-border">
          <NavLink to="/dashboard" className="block" data-testid="sidebar-brand-link">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-primary flex items-center justify-center rounded-sm">
                <Globe className="w-4 h-4 text-white" />
              </div>
              <div className="font-heading font-black tracking-tighter text-lg text-white leading-none">
                SphereInvest<span className="text-primary"> AI</span>
              </div>
            </div>
            <div className="overline-label mt-2">Research Terminal</div>
          </NavLink>
        </div>

        <nav className="flex-1 py-4 overflow-y-auto">
          <div className="overline-label px-5 mb-2">Modules</div>
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === "/"}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-2.5 text-sm border-l-2 transition-colors duration-150 ${
                    isActive
                      ? "border-primary bg-white/5 text-white font-medium"
                      : item.soon
                      ? "border-transparent text-muted-foreground/50 hover:text-muted-foreground hover:bg-white/[0.02]"
                      : "border-transparent text-muted-foreground hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                <span className="flex-1">{item.label}</span>
                {item.soon && <Lock className="w-3 h-3 opacity-60" />}
              </NavLink>
            );
          })}
        </nav>

        {user?.tier !== "premium" && (
          <div className="px-4 py-3 border-t border-border">
            <NavLink to="/upgrade" data-testid="nav-upgrade"
              className="flex items-center gap-2.5 px-3 py-2.5 rounded-sm bg-primary/15 border border-primary/40 text-primary hover:bg-primary/25 transition-colors duration-150">
              <Crown className="w-4 h-4 text-yellow-500" />
              <span className="text-sm font-medium flex-1">Upgrade to Premium</span>
            </NavLink>
          </div>
        )}

        <div className="px-5 py-4 border-t border-border">
          <p className="text-[11px] leading-relaxed text-muted-foreground/70">
            Research tools only. Nothing here is financial advice.
          </p>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 ml-60 flex flex-col min-h-screen">
        <header className="sticky top-0 z-30 h-14 border-b border-white/10 backdrop-blur-xl bg-black/60 flex items-center justify-between px-6">
          <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
            <span className="w-1.5 h-1.5 rounded-full bg-gain live-dot" />
            <span data-testid="header-status-text">LIVE CACHE · EQUITY RESEARCH</span>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-9 w-9 p-0 relative text-muted-foreground hover:text-white rounded-sm"
                  data-testid="alerts-bell-trigger" title="Watchlist alerts">
                  {alerts.length > 0 ? <BellRing className="w-4 h-4 text-yellow-500" /> : <Bell className="w-4 h-4" />}
                  {alerts.length > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-yellow-500 text-black text-[10px] font-mono flex items-center justify-center rounded-sm"
                      data-testid="alerts-count-badge">
                      {alerts.length}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72 rounded-sm">
                <DropdownMenuLabel className="overline-label">Triggered alerts</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {alerts.length === 0 ? (
                  <DropdownMenuItem disabled className="text-xs text-muted-foreground">No alerts triggered</DropdownMenuItem>
                ) : (
                  alerts.map((a, i) => (
                    <DropdownMenuItem key={i} className="cursor-pointer flex-col items-start gap-0.5"
                      data-testid={`triggered-alert-item-${a.ticker}`} onClick={() => navigate("/watchlists")}>
                      <span className="font-mono text-sm text-yellow-500">{a.ticker}</span>
                      <span className="text-xs text-muted-foreground">{a.messages.join(" · ")} — {a.watchlist_name}</span>
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-9 gap-2 text-sm text-muted-foreground hover:text-white rounded-sm"
                data-testid="user-menu-trigger"
              >
                <span className="w-6 h-6 rounded-sm bg-primary/20 text-primary flex items-center justify-center font-mono text-xs uppercase">
                  {(user?.name || user?.email || "?").charAt(0)}
                </span>
                <span className="hidden sm:inline max-w-[180px] truncate">{user?.email}</span>
                <ChevronDown className="w-3.5 h-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 rounded-sm">
              <DropdownMenuLabel className="font-mono text-xs">
                {user?.name} · {user?.role}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="my-shared-cards-menu-item"
                className="cursor-pointer"
                onClick={() => navigate("/shared-cards")}
              >
                <Share2 className="w-4 h-4 mr-2" /> My shared cards
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="change-password-menu-item"
                className="cursor-pointer"
                onClick={() => setPwOpen(true)}
              >
                <KeyRound className="w-4 h-4 mr-2" /> Change password
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="delete-account-menu-item"
                className="text-loss focus:text-loss cursor-pointer"
                onClick={() => setDelOpen(true)}
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete my account
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="logout-button"
                className="text-loss focus:text-loss cursor-pointer"
                onClick={async () => {
                  await logout();
                  navigate("/login");
                }}
              >
                <LogOut className="w-4 h-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-6 lg:p-8">
          <Outlet />
        </main>

        <footer className="border-t border-border px-6 py-3 space-y-1.5">
          <p className="text-[11px] font-mono text-muted-foreground/60" data-testid="data-source-footer">
            Market data: Yahoo Finance (delayed/EOD), cached up to 12h. Research insights are AI-generated — not
            financial advice.{" "}
            <NavLink to="/methodology" className="text-primary/80 hover:text-primary underline-offset-2 hover:underline">
              How the analysis works
            </NavLink>
          </p>
          <p className="text-[11px] font-mono text-muted-foreground/50" data-testid="compliance-footer">
            <NavLink to="/methodology" className="hover:text-muted-foreground">Methodology</NavLink> ·{" "}
            <NavLink to="/privacy" className="hover:text-muted-foreground">Privacy</NavLink> ·{" "}
            <NavLink to="/terms" className="hover:text-muted-foreground">Terms</NavLink> ·{" "}
            <NavLink to="/disclaimer" className="hover:text-muted-foreground">Disclaimer</NavLink>
          </p>
        </footer>
      </div>

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      <DeleteAccountDialog open={delOpen} onOpenChange={setDelOpen} />
    </div>
  );
};
